import { useState, useEffect } from 'react';
import { Calendar, X, Pin, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { ChatFolder } from '@/services/folderService';
import { format, subDays, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';

export interface AdvancedFilters {
  datePreset: 'any' | 'week' | 'month' | '3months' | 'custom';
  customDateRange: { from: Date; to: Date } | null;
  folders: string[];
  searchInContent: boolean;
  pinnedOnly: boolean;
}

interface AdvancedSearchFiltersProps {
  folders: ChatFolder[];
  filters: AdvancedFilters;
  onFiltersChange: (filters: AdvancedFilters) => void;
  onClose: () => void;
}

const DEFAULT_FILTERS: AdvancedFilters = {
  datePreset: 'any',
  customDateRange: null,
  folders: [],
  searchInContent: false,
  pinnedOnly: false,
};

export function AdvancedSearchFilters({
  folders,
  filters,
  onFiltersChange,
  onClose,
}: AdvancedSearchFiltersProps) {
  const [localFilters, setLocalFilters] = useState<AdvancedFilters>(filters);
  const [fromCalendarOpen, setFromCalendarOpen] = useState(false);
  const [toCalendarOpen, setToCalendarOpen] = useState(false);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleDatePresetChange = (preset: AdvancedFilters['datePreset']) => {
    setLocalFilters(prev => ({
      ...prev,
      datePreset: preset,
      customDateRange: preset === 'custom' ? prev.customDateRange : null,
    }));
  };

  const handleFolderToggle = (folderId: string) => {
    setLocalFilters(prev => ({
      ...prev,
      folders: prev.folders.includes(folderId)
        ? prev.folders.filter(id => id !== folderId)
        : [...prev.folders, folderId],
    }));
  };

  const handleSelectAllFolders = () => {
    setLocalFilters(prev => ({
      ...prev,
      folders: prev.folders.length === folders.length + 1 
        ? [] 
        : ['uncategorized', ...folders.map(f => f.id)],
    }));
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleClear = () => {
    setLocalFilters(DEFAULT_FILTERS);
    onFiltersChange(DEFAULT_FILTERS);
    onClose();
  };

  const getDateRange = (): { from: Date; to: Date } | null => {
    const now = new Date();
    switch (localFilters.datePreset) {
      case 'week':
        return { from: subDays(now, 7), to: now };
      case 'month':
        return { from: subMonths(now, 1), to: now };
      case '3months':
        return { from: subMonths(now, 3), to: now };
      case 'custom':
        return localFilters.customDateRange;
      default:
        return null;
    }
  };

  const hasActiveFilters = 
    localFilters.datePreset !== 'any' ||
    localFilters.folders.length > 0 ||
    localFilters.searchInContent ||
    localFilters.pinnedOnly;

  return (
    <div className="p-4 space-y-4 bg-popover rounded-lg border border-border shadow-lg">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Filtros avanzados
        </h4>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Date Filter */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          Fecha
        </Label>
        <RadioGroup 
          value={localFilters.datePreset} 
          onValueChange={(value) => handleDatePresetChange(value as AdvancedFilters['datePreset'])}
          className="space-y-1"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="any" id="date-any" />
            <Label htmlFor="date-any" className="text-sm font-normal cursor-pointer">
              Cualquier fecha
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="week" id="date-week" />
            <Label htmlFor="date-week" className="text-sm font-normal cursor-pointer">
              Última semana
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="month" id="date-month" />
            <Label htmlFor="date-month" className="text-sm font-normal cursor-pointer">
              Último mes
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="3months" id="date-3months" />
            <Label htmlFor="date-3months" className="text-sm font-normal cursor-pointer">
              Últimos 3 meses
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="custom" id="date-custom" />
            <Label htmlFor="date-custom" className="text-sm font-normal cursor-pointer">
              Rango personalizado
            </Label>
          </div>
        </RadioGroup>

        {localFilters.datePreset === 'custom' && (
          <div className="flex items-center gap-2 mt-2">
            <Popover open={fromCalendarOpen} onOpenChange={setFromCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs h-8">
                  {localFilters.customDateRange?.from 
                    ? format(localFilters.customDateRange.from, 'dd/MM/yyyy', { locale: es })
                    : 'Desde'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={localFilters.customDateRange?.from}
                  onSelect={(date) => {
                    if (date) {
                      setLocalFilters(prev => ({
                        ...prev,
                        customDateRange: {
                          from: date,
                          to: prev.customDateRange?.to || new Date(),
                        },
                      }));
                      setFromCalendarOpen(false);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <span className="text-xs text-muted-foreground">-</span>
            <Popover open={toCalendarOpen} onOpenChange={setToCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs h-8">
                  {localFilters.customDateRange?.to 
                    ? format(localFilters.customDateRange.to, 'dd/MM/yyyy', { locale: es })
                    : 'Hasta'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={localFilters.customDateRange?.to}
                  onSelect={(date) => {
                    if (date) {
                      setLocalFilters(prev => ({
                        ...prev,
                        customDateRange: {
                          from: prev.customDateRange?.from || subMonths(new Date(), 1),
                          to: date,
                        },
                      }));
                      setToCalendarOpen(false);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Folder Filter */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">📁 Carpeta</Label>
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="folder-all" 
              checked={localFilters.folders.length === folders.length + 1}
              onCheckedChange={handleSelectAllFolders}
            />
            <Label htmlFor="folder-all" className="text-sm font-normal cursor-pointer">
              Todas
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="folder-uncategorized" 
              checked={localFilters.folders.includes('uncategorized')}
              onCheckedChange={() => handleFolderToggle('uncategorized')}
            />
            <Label htmlFor="folder-uncategorized" className="text-sm font-normal cursor-pointer">
              📥 Sin categoría
            </Label>
          </div>
          {folders.map(folder => (
            <div key={folder.id} className="flex items-center space-x-2">
              <Checkbox 
                id={`folder-${folder.id}`} 
                checked={localFilters.folders.includes(folder.id)}
                onCheckedChange={() => handleFolderToggle(folder.id)}
              />
              <Label htmlFor={`folder-${folder.id}`} className="text-sm font-normal cursor-pointer">
                <span className="mr-1">{folder.icon}</span>
                <span style={{ color: folder.color }}>{folder.name}</span>
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Search In */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">📝 Buscar en</Label>
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="search-content" 
              checked={localFilters.searchInContent}
              onCheckedChange={(checked) => 
                setLocalFilters(prev => ({ ...prev, searchInContent: !!checked }))
              }
            />
            <Label htmlFor="search-content" className="text-sm font-normal cursor-pointer">
              Contenido de mensajes
            </Label>
          </div>
        </div>
      </div>

      {/* Pinned Only */}
      <div className="flex items-center space-x-2">
        <Checkbox 
          id="pinned-only" 
          checked={localFilters.pinnedOnly}
          onCheckedChange={(checked) => 
            setLocalFilters(prev => ({ ...prev, pinnedOnly: !!checked }))
          }
        />
        <Label htmlFor="pinned-only" className="text-sm font-normal cursor-pointer flex items-center gap-1">
          <Pin className="h-3 w-3" />
          Solo fijados
        </Label>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleClear}
          disabled={!hasActiveFilters}
        >
          Limpiar filtros
        </Button>
        <Button size="sm" onClick={handleApply}>
          Aplicar
        </Button>
      </div>
    </div>
  );
}

export function getDefaultFilters(): AdvancedFilters {
  return DEFAULT_FILTERS;
}
